import {ElementPosition} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {Icon} from '@iconify/react';

import React, {useCallback, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {PeriodLabel} from '../../../util/enums';

import {PortalSelectOption, SearchRankingMode} from '../../../util/types';

import BolmateButton from '../../controls/button/bolmateButton';

import BolmateSegmentedToggle from '../../controls/button/bolmateSegmentedToggle';

import BolmatePeriodSelector from '../../controls/periodSelector/bolmatePeriodSelector';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import BolmateSearchInput from '../../controls/search/bolmateSearchInput';

import BolmateTooltip from '../../controls/tooltip/bolmateTooltip';

interface Props {
    onSearchChange: (value: string) => void;
    rankingMode: SearchRankingMode;
    onRankingModeChange: (mode: SearchRankingMode) => void;
    selectedCountries: Array<'NL' | 'BE'>;
    onCountryChange: (value: string) => void;
    selectedStoreIds: Array<string>;
    storeItems: Array<PortalSelectOption>;
    onStoreChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    periodValue: [IDate | null, IDate | null];
    selectedPreset: PeriodLabel | null;
    onPeriodChange: (range: [IDate, IDate], preset: PeriodLabel | null) => void;
    onExportExcel: VoidFunction;
    exportingExcel: boolean;
    visibleColumnFields: Array<string>;
    allColumnItems: Array<PortalSelectOption>;
    onColumnsChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    onImportClick: VoidFunction;
    manualKeywordsCount: number;
    manualKeywordsLimit: number | null;
}

const flag = (code: string) => <span className={`fi fi-${code}`} style={{margin: '0 0.375rem'}} />;
const countryItems: Array<PortalSelectOption> = [
    {value: 'NL', label: <span>{flag('nl')} NL</span>},
    {value: 'BE', label: <span>{flag('be')} BE</span>},
    {
        value: 'NL,BE',
        label: (
            <span>
                {flag('nl')} & {flag('be')}
            </span>
        )
    }
];

export default function SearchRankingsControlBar(props: Props) {
    const {t} = useTranslation();
    const RANKING_MODE_OPTIONS = useMemo(
        () => [
            {value: 'average' as SearchRankingMode, label: t('searchRankings.controlBar.avg', 'Gem.')},
            {value: 'best' as SearchRankingMode, label: t('searchRankings.controlBar.best', 'Beste')}
        ],
        [t]
    );
    const {
        onSearchChange,
        rankingMode,
        onRankingModeChange,
        selectedCountries,
        onCountryChange,
        selectedStoreIds,
        storeItems,
        onStoreChange,
        periodValue,
        selectedPreset,
        onPeriodChange,
        visibleColumnFields,
        allColumnItems,
        onColumnsChange,
        onImportClick,
        manualKeywordsCount,
        manualKeywordsLimit
    } = props;
    const importDisabled = useMemo(() => {
        if (manualKeywordsLimit === null) return false;
        return manualKeywordsCount >= manualKeywordsLimit;
    }, [manualKeywordsCount, manualKeywordsLimit]);

    const handleCountryChange = useCallback(
        (value: string | null) => {
            onCountryChange(value ?? 'NL');
        },
        [onCountryChange]
    );

    return (
        <div className='search-rankings-control-bar'>
            {/* Left: Search */}
            <div className='control-bar-left'>
                <BolmateSearchInput
                    onChange={onSearchChange}
                    placeholder={t('searchRankings.controlBar.searchProducts', 'Zoek producten...')}
                    debounce={500}
                    size='small'
                />
            </div>

            {/* Right: Toggle + Country + Stores + Columns + Period + Import + Export */}
            <div className='control-bar-right'>
                {/* Ranking Mode Toggle */}
                <BolmateSegmentedToggle
                    options={RANKING_MODE_OPTIONS}
                    value={rankingMode}
                    onChange={onRankingModeChange}
                />

                {/* Country Selector */}
                <PortalSelectController
                    onChange={handleCountryChange}
                    value={selectedCountries.join(',')}
                    items={countryItems}
                    label={t('searchRankings.controlBar.country', 'Land')}
                    size='small'
                    shape='rounded'
                />

                {/* Store Selector */}
                {storeItems.length > 0 && (
                    <PortalSelectController
                        key={storeItems.map(s => s.value).join(',')}
                        onChange={onStoreChange}
                        defaultValue={selectedStoreIds.length > 0 ? selectedStoreIds : storeItems.map(s => s.value)}
                        items={storeItems}
                        label={t('searchRankings.controlBar.stores', 'Winkels')}
                        placeholder={t('searchRankings.controlBar.allStores', 'Alle winkels')}
                        size='small'
                        shape='rounded'
                        multiple
                        controls={['selectAll', 'deselectAll']}
                        parseSelectValue={(values: Array<string>) => {
                            if (values.length === 0 || values.length === storeItems.length)
                                return t('searchRankings.controlBar.allStores', 'Alle winkels');
                            if (values.length === 1) return t('searchRankings.controlBar.oneStore', '1 winkel');
                            return t('searchRankings.controlBar.storesCount', '{{count}} winkels', {
                                count: values.length
                            });
                        }}
                    />
                )}

                {/* Column Selector */}
                <PortalSelectController
                    onChange={onColumnsChange}
                    value={visibleColumnFields}
                    items={allColumnItems}
                    label={t('searchRankings.controlBar.columns', 'Kolommen')}
                    placeholder={t('searchRankings.controlBar.columns', 'Kolommen')}
                    size='small'
                    shape='rounded'
                    multiple
                    controls={['selectAll', 'deselectAll']}
                    itemsHeight='30rem'
                    disableAutoScroll
                    parseSelectValue={(values: Array<string>) => {
                        if (values.length === 1) return t('searchRankings.controlBar.oneColumn', '1 kolom');
                        return t('searchRankings.controlBar.columnsCount', '{{count}} kolommen', {
                            count: values.length
                        });
                    }}
                />

                {/* Period Selector */}
                <BolmatePeriodSelector
                    onChange={onPeriodChange}
                    value={periodValue}
                    selectedPreset={selectedPreset}
                    label={t('searchRankings.controlBar.period', 'Periode')}
                    selectSize='small'
                    selectShape='rounded'
                />

                {/* Import CSV */}
                <BolmateButton
                    onClick={onImportClick}
                    label={t('searchRankings.controlBar.import', 'KeywordsImport')}
                    tertiary
                    color='blue'
                    size='small'
                    shape='rounded'
                    ornaments={{
                        element: <Icon icon='mdi:file-upload-outline' style={{fontSize: '1rem'}} />,
                        placement: ElementPosition.LEFT
                    }}
                    disabled={importDisabled}
                />

                {/* Export Excel */}
                <BolmateTooltip text={t('searchRankings.controlBar.comingSoon', 'Binnenkort beschikbaar')}>
                    <span>
                        <BolmateButton
                            onClick={() => {}}
                            label={t('searchRankings.controlBar.export', 'Export')}
                            disabled
                            tertiary
                            color='green'
                            size='small'
                            shape='rounded'
                            ornaments={{
                                element: <Icon icon='mdi:file-excel-outline' style={{fontSize: '1rem'}} />,
                                placement: ElementPosition.LEFT
                            }}
                        />
                    </span>
                </BolmateTooltip>
            </div>
        </div>
    );
}
